import React, { useEffect, useState, useCallback } from 'react';
import {
  Leaf, Scissors, Droplets, Apple, Truck, Shield, Eye,
  Plus, Check, ChevronRight,
} from 'lucide-react';
import toast from '../../../utils/toast';
import {
  plantationService,
  PlantationOperation,
  PlantationCampagne,
  PlantationParcelle,
} from '../../../services/plantation.service';

// ── Types ──────────────────────────────────────────────────────────────────────

interface OperationsTabProps {
  entiteId: string;
  campagnes: PlantationCampagne[];
  parcelles: PlantationParcelle[];
  campagneActive?: string;
}

type TypeOperation =
  | 'entretien_sol'
  | 'elagage'
  | 'fertilisation'
  | 'recolte'
  | 'collecte_regimes'
  | 'transport'
  | 'supervision';

const TYPE_LABELS: Record<string, string> = {
  entretien_sol: 'Entretien sol',
  elagage: 'Élagage',
  fertilisation: 'Fertilisation',
  recolte: 'Récolte',
  collecte_regimes: 'Collecte régimes',
  transport: 'Transport',
  supervision: 'Supervision',
};

const TYPE_COLORS: Record<string, string> = {
  entretien_sol: 'bg-green-100 text-green-800',
  elagage: 'bg-teal-100 text-teal-800',
  fertilisation: 'bg-amber-100 text-amber-800',
  recolte: 'bg-orange-100 text-orange-800',
  collecte_regimes: 'bg-red-100 text-red-800',
  transport: 'bg-blue-100 text-blue-800',
  supervision: 'bg-purple-100 text-purple-800',
};

const TYPE_ICONS: Record<string, React.ReactNode> = {
  entretien_sol: <Leaf size={14} />,
  elagage: <Scissors size={14} />,
  fertilisation: <Droplets size={14} />,
  recolte: <Apple size={14} />,
  collecte_regimes: <ChevronRight size={14} />,
  transport: <Truck size={14} />,
  supervision: <Shield size={14} />,
};

// ── Champs dynamiques par type ─────────────────────────────────────────────────

type FormData = Partial<PlantationOperation> & {
  typeOperation: string;
  date: string;
};

const emptyForm = (): FormData => ({
  typeOperation: '',
  date: new Date().toISOString().slice(0, 10),
  parcelleId: '',
  campagneId: '',
  superficiePlanifiee: undefined,
  nombreMO: undefined,
  matriculeCEAPI: '',
  nombrePiedsPlanifies: undefined,
  tonnagePlanifie: undefined,
  nombreSacsPlanifies: undefined,
  produitFertilisant: '',
  nombreRegimesCollectes: undefined,
  nombreRegimesBons: undefined,
  nombreRegimesVerts: undefined,
  nombreRegimesPourris: undefined,
  nombreRegimesAnormaux: undefined,
  vehiculeImmatriculation: '',
  chauffeurMatricule: '',
  observations: '',
});

const emptyRealiseForm = (): Partial<PlantationOperation> => ({
  superficieRealisee: undefined,
  nombreMO: undefined,
  matriculeCEAPI: '',
  nombrePiedsRealises: undefined,
  tonnageRealise: undefined,
  nombreSacsRealises: undefined,
  produitFertilisant: '',
  nombreRegimesCollectes: undefined,
  nombreRegimesBons: undefined,
  nombreRegimesVerts: undefined,
  nombreRegimesPourris: undefined,
  nombreRegimesAnormaux: undefined,
  vehiculeImmatriculation: '',
  chauffeurMatricule: '',
  observations: '',
});

// ── Helpers ───────────────────────────────────────────────────────────────────

const numField = (
  label: string,
  field: string,
  value: number | undefined,
  onChange: (f: string, v: number | undefined) => void,
  suffix?: string,
) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}{suffix && ` (${suffix})`}</label>
    <input
      type="number"
      min={0}
      value={value ?? ''}
      onChange={e => onChange(field, e.target.value === '' ? undefined : Number(e.target.value))}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
    />
  </div>
);

const txtField = (
  label: string,
  field: string,
  value: string | undefined,
  onChange: (f: string, v: string) => void,
) => (
  <div>
    <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
    <input
      type="text"
      value={value ?? ''}
      onChange={e => onChange(field, e.target.value)}
      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
    />
  </div>
);

// ── Champs planifié selon type ─────────────────────────────────────────────────

function ChampsPlanifie({ type, form, onChange }: {
  type: string;
  form: FormData;
  onChange: (field: string, value: unknown) => void;
}) {
  const n = (label: string, field: string, suffix?: string) =>
    numField(label, field, (form as unknown as Record<string, number | undefined>)[field], onChange as (f: string, v: number | undefined) => void, suffix);
  const t = (label: string, field: string) =>
    txtField(label, field, (form as unknown as Record<string, string | undefined>)[field], onChange as (f: string, v: string) => void);

  if (type === 'entretien_sol') return (
    <>
      {n('Superficie planifiée', 'superficiePlanifiee', 'ha')}
      {n('Nombre MO', 'nombreMO')}
      {t('Matricule CEAPI', 'matriculeCEAPI')}
    </>
  );

  if (type === 'elagage') return (
    <>
      {n('Nombre pieds planifiés', 'nombrePiedsPlanifies')}
      {n('Superficie planifiée', 'superficiePlanifiee', 'ha')}
      {n('Nombre MO', 'nombreMO')}
      {t('Matricule CEAPI', 'matriculeCEAPI')}
    </>
  );

  if (type === 'fertilisation') return (
    <>
      {t('Produit fertilisant', 'produitFertilisant')}
      {n('Tonnage planifié', 'tonnagePlanifie', 'T')}
      {n('Nombre sacs planifiés', 'nombreSacsPlanifies')}
      {n('Superficie planifiée', 'superficiePlanifiee', 'ha')}
      {t('Matricule CEAPI', 'matriculeCEAPI')}
      {n('Nombre MO', 'nombreMO')}
    </>
  );

  if (type === 'recolte') return (
    <>
      {n('Superficie planifiée', 'superficiePlanifiee', 'ha')}
      {t('Matricule CEAPI', 'matriculeCEAPI')}
      {n('Nombre MO', 'nombreMO')}
    </>
  );

  if (type === 'collecte_regimes') return (
    <>
      {n('Régimes collectés', 'nombreRegimesCollectes')}
      {n('Régimes bons', 'nombreRegimesBons')}
      {n('Régimes verts', 'nombreRegimesVerts')}
      {n('Régimes pourris', 'nombreRegimesPourris')}
      {n('Régimes anormaux', 'nombreRegimesAnormaux')}
      {t('Immatriculation véhicule', 'vehiculeImmatriculation')}
      {t('Matricule chauffeur', 'chauffeurMatricule')}
    </>
  );

  if (type === 'transport') return (
    <>
      {t('Immatriculation véhicule', 'vehiculeImmatriculation')}
      {t('Matricule chauffeur', 'chauffeurMatricule')}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Observations (kilométrage, trajet...)</label>
        <textarea
          value={form.observations ?? ''}
          onChange={e => onChange('observations', e.target.value)}
          rows={3}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
        />
      </div>
    </>
  );

  if (type === 'supervision') return (
    <>
      {t('Matricule superviseur (CEAPI)', 'matriculeCEAPI')}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Observations</label>
        <textarea
          value={form.observations ?? ''}
          onChange={e => onChange('observations', e.target.value)}
          rows={3}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
        />
      </div>
    </>
  );

  return null;
}

// ── Champs réalisé selon type ──────────────────────────────────────────────────

function ChampsRealise({ type, form, onChange }: {
  type: string;
  form: Partial<PlantationOperation>;
  onChange: (field: string, value: unknown) => void;
}) {
  const n = (label: string, field: string, suffix?: string) =>
    numField(label, field, (form as unknown as Record<string, number | undefined>)[field], onChange as (f: string, v: number | undefined) => void, suffix);
  const t = (label: string, field: string) =>
    txtField(label, field, (form as unknown as Record<string, string | undefined>)[field], onChange as (f: string, v: string) => void);

  if (type === 'entretien_sol') return (
    <>
      {n('Superficie réalisée', 'superficieRealisee', 'ha')}
      {n('Nombre MO', 'nombreMO')}
      {t('Matricule CEAPI', 'matriculeCEAPI')}
    </>
  );

  if (type === 'elagage') return (
    <>
      {n('Nombre pieds réalisés', 'nombrePiedsRealises')}
      {n('Superficie réalisée', 'superficieRealisee', 'ha')}
      {n('Nombre MO', 'nombreMO')}
      {t('Matricule CEAPI', 'matriculeCEAPI')}
    </>
  );

  if (type === 'fertilisation') return (
    <>
      {t('Produit fertilisant', 'produitFertilisant')}
      {n('Tonnage réalisé', 'tonnageRealise', 'T')}
      {n('Nombre sacs réalisés', 'nombreSacsRealises')}
      {n('Superficie réalisée', 'superficieRealisee', 'ha')}
      {t('Matricule CEAPI', 'matriculeCEAPI')}
      {n('Nombre MO', 'nombreMO')}
    </>
  );

  if (type === 'recolte') return (
    <>
      {n('Superficie réalisée', 'superficieRealisee', 'ha')}
      {t('Matricule CEAPI', 'matriculeCEAPI')}
      {n('Nombre MO', 'nombreMO')}
    </>
  );

  if (type === 'collecte_regimes') return (
    <>
      {n('Régimes collectés', 'nombreRegimesCollectes')}
      {n('Régimes bons', 'nombreRegimesBons')}
      {n('Régimes verts', 'nombreRegimesVerts')}
      {n('Régimes pourris', 'nombreRegimesPourris')}
      {n('Régimes anormaux', 'nombreRegimesAnormaux')}
      {t('Immatriculation véhicule', 'vehiculeImmatriculation')}
      {t('Matricule chauffeur', 'chauffeurMatricule')}
    </>
  );

  if (type === 'transport') return (
    <>
      {t('Immatriculation véhicule', 'vehiculeImmatriculation')}
      {t('Matricule chauffeur', 'chauffeurMatricule')}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Observations (kilométrage, trajet...)</label>
        <textarea
          value={form.observations ?? ''}
          onChange={e => onChange('observations', e.target.value)}
          rows={3}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
        />
      </div>
    </>
  );

  if (type === 'supervision') return (
    <>
      {t('Matricule superviseur (CEAPI)', 'matriculeCEAPI')}
      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">Observations</label>
        <textarea
          value={form.observations ?? ''}
          onChange={e => onChange('observations', e.target.value)}
          rows={3}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
        />
      </div>
    </>
  );

  return null;
}

// ── Colonne spécifique au type dans le tableau ─────────────────────────────────

function CellSpecifique({ op }: { op: PlantationOperation }) {
  const t = op.typeOperation;
  if (t === 'entretien_sol' || t === 'recolte')
    return <span className="text-xs text-gray-600">{op.nombreMO ? `${op.nombreMO} MO` : '—'}</span>;
  if (t === 'elagage')
    return (
      <span className="text-xs text-gray-600">
        {op.phase === 'planifie' && op.nombrePiedsPlanifies != null ? `${op.nombrePiedsPlanifies} pieds` : ''}
        {op.phase === 'realise' && op.nombrePiedsRealises != null ? `${op.nombrePiedsRealises} pieds` : ''}
        {op.nombrePiedsPlanifies == null && op.nombrePiedsRealises == null ? '—' : ''}
      </span>
    );
  if (t === 'fertilisation')
    return (
      <span className="text-xs text-gray-600">
        {op.produitFertilisant ?? ''}
        {(op.tonnagePlanifie ?? op.tonnageRealise) != null
          ? ` — ${op.tonnagePlanifie ?? op.tonnageRealise} T`
          : ''}
      </span>
    );
  if (t === 'collecte_regimes')
    return <span className="text-xs text-gray-600">{op.nombreRegimesCollectes != null ? `${op.nombreRegimesCollectes} régimes` : '—'}</span>;
  if (t === 'transport')
    return <span className="text-xs text-gray-600">{op.vehiculeImmatriculation ?? '—'}</span>;
  if (t === 'supervision')
    return <span className="text-xs text-gray-600">{op.matriculeCEAPI ?? '—'}</span>;
  return <span className="text-xs text-gray-400">—</span>;
}

// ── Composant principal ────────────────────────────────────────────────────────

export default function OperationsTab({
  entiteId,
  campagnes,
  parcelles,
  campagneActive,
}: OperationsTabProps) {
  const today = new Date().toISOString().slice(0, 10);

  // Filtres
  const [filtreDate, setFiltreDate] = useState(today);
  const [filtreType, setFiltreType] = useState('');
  const [filtrePhase, setFiltrePhase] = useState('');

  // Données
  const [operations, setOperations] = useState<PlantationOperation[]>([]);
  const [loading, setLoading] = useState(false);

  // Modals
  const [showPlanifier, setShowPlanifier] = useState(false);
  const [showRealiser, setShowRealiser] = useState<PlantationOperation | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Formulaire planification
  const [form, setForm] = useState<FormData>(emptyForm);

  // Formulaire réalisation
  const [realiseForm, setRealiseForm] = useState<Partial<PlantationOperation>>(emptyRealiseForm());

  // Flat list de parcelles pour le select
  const flatParcelles = React.useMemo(() => {
    const result: PlantationParcelle[] = [];
    const flatten = (nodes: PlantationParcelle[]) => {
      for (const n of nodes) {
        result.push(n);
        if (n.children) flatten(n.children);
      }
    };
    flatten(parcelles);
    return result;
  }, [parcelles]);

  const charger = useCallback(async () => {
    setLoading(true);
    try {
      const data = await plantationService.listOperations(entiteId, {
        date: filtreDate || undefined,
        typeOperation: filtreType || undefined,
        phase: filtrePhase || undefined,
        campagneId: campagneActive || undefined,
      });
      setOperations(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Impossible de charger les opérations');
    } finally {
      setLoading(false);
    }
  }, [entiteId, filtreDate, filtreType, filtrePhase, campagneActive]);

  useEffect(() => {
    charger();
  }, [charger]);

  // ── Handlers formulaire planification ──────────────────────────────────────

  const handleFormChange = (field: string, value: unknown) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleTypeChange = (type: string) => {
    setForm({ ...emptyForm(), typeOperation: type, date: form.date, parcelleId: form.parcelleId, campagneId: form.campagneId });
  };

  const handlePlanifier = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.typeOperation) { toast.error('Sélectionnez un type d\'opération'); return; }
    if (!form.date) { toast.error('La date est obligatoire'); return; }
    setSubmitting(true);
    try {
      await plantationService.createOperation({
        ...form,
        entiteId,
        phase: 'planifie',
        statut: 'planifie',
        campagneId: form.campagneId || campagneActive || undefined,
      });
      toast.success('Opération planifiée avec succès');
      setShowPlanifier(false);
      setForm(emptyForm());
      charger();
    } catch {
      toast.error('Erreur lors de la planification');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Handlers réalisation ───────────────────────────────────────────────────

  const ouvrirRealiser = (op: PlantationOperation) => {
    setRealiseForm({
      superficieRealisee: op.superficiePlanifiee,
      nombreMO: op.nombreMO,
      matriculeCEAPI: op.matriculeCEAPI ?? '',
      nombrePiedsRealises: op.nombrePiedsPlanifies,
      tonnageRealise: op.tonnagePlanifie,
      nombreSacsRealises: op.nombreSacsPlanifies,
      produitFertilisant: op.produitFertilisant ?? '',
      nombreRegimesCollectes: op.nombreRegimesCollectes,
      nombreRegimesBons: op.nombreRegimesBons,
      nombreRegimesVerts: op.nombreRegimesVerts,
      nombreRegimesPourris: op.nombreRegimesPourris,
      nombreRegimesAnormaux: op.nombreRegimesAnormaux,
      vehiculeImmatriculation: op.vehiculeImmatriculation ?? '',
      chauffeurMatricule: op.chauffeurMatricule ?? '',
      observations: op.observations ?? '',
    });
    setShowRealiser(op);
  };

  const handleRealiseChange = (field: string, value: unknown) => {
    setRealiseForm(prev => ({ ...prev, [field]: value }));
  };

  const handleRealiser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!showRealiser) return;
    setSubmitting(true);
    try {
      await plantationService.realiserOperation(showRealiser.id, {
        ...realiseForm,
        entiteId,
        phase: 'realise',
        statut: 'realise',
        date: showRealiser.date,
        typeOperation: showRealiser.typeOperation,
        parcelleId: showRealiser.parcelleId,
        campagneId: showRealiser.campagneId,
      });
      toast.success('Réalisation enregistrée');
      setShowRealiser(null);
      charger();
    } catch {
      toast.error('Erreur lors de la saisie du réalisé');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Validation ─────────────────────────────────────────────────────────────

  const handleValider = async (op: PlantationOperation) => {
    try {
      await plantationService.validerOperation(op.id);
      toast.success('Opération validée');
      charger();
    } catch {
      toast.error('Erreur lors de la validation');
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* Filtres */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Date</label>
            <input
              type="date"
              value={filtreDate}
              onChange={e => setFiltreDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Type d'opération</label>
            <select
              value={filtreType}
              onChange={e => setFiltreType(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="">Tous</option>
              {Object.entries(TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Phase</label>
            <select
              value={filtrePhase}
              onChange={e => setFiltrePhase(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              <option value="">Tous</option>
              <option value="planifie">Planifié</option>
              <option value="realise">Réalisé</option>
            </select>
          </div>
          <button
            onClick={() => { setForm(emptyForm()); setShowPlanifier(true); }}
            className="ml-auto flex items-center gap-2 bg-teal-700 hover:bg-teal-800 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            <Plus size={16} />
            Planifier
          </button>
        </div>
      </div>

      {/* Tableau */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-16 text-gray-400 text-sm">Chargement...</div>
        ) : operations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <Leaf size={40} className="mb-3 opacity-30" />
            <p className="text-sm">Aucune opération pour les filtres sélectionnés</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Phase</th>
                  <th className="px-4 py-3">Parcelle</th>
                  <th className="px-4 py-3">Superficie Plan./Réal.</th>
                  <th className="px-4 py-3">Spécifique</th>
                  <th className="px-4 py-3">Statut</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {operations.map(op => {
                  const parcelleNom = flatParcelles.find(p => p.id === op.parcelleId)?.nom
                    ?? op.parcelle?.nom ?? '—';
                  return (
                    <tr key={op.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-700">
                        {new Date(op.date).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${TYPE_COLORS[op.typeOperation] ?? 'bg-gray-100 text-gray-700'}`}>
                          {TYPE_ICONS[op.typeOperation]}
                          {TYPE_LABELS[op.typeOperation] ?? op.typeOperation}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${op.phase === 'planifie' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>
                          {op.phase === 'planifie' ? 'Planifié' : 'Réalisé'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 text-xs">{parcelleNom}</td>
                      <td className="px-4 py-3 text-gray-600 text-xs">
                        {op.superficiePlanifiee != null ? `${op.superficiePlanifiee} ha` : '—'}
                        {op.superficieRealisee != null ? ` / ${op.superficieRealisee} ha` : ''}
                      </td>
                      <td className="px-4 py-3">
                        <CellSpecifique op={op} />
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                          op.statut === 'valide' ? 'bg-emerald-100 text-emerald-700'
                          : op.statut === 'planifie' ? 'bg-blue-100 text-blue-700'
                          : op.statut === 'realise' ? 'bg-amber-100 text-amber-700'
                          : 'bg-gray-100 text-gray-600'
                        }`}>
                          {op.statut}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 justify-end">
                          {op.phase === 'planifie' && op.statut !== 'valide' && (
                            <button
                              onClick={() => ouvrirRealiser(op)}
                              title="Saisir le réalisé"
                              className="flex items-center gap-1 px-2 py-1 rounded bg-teal-50 hover:bg-teal-100 text-teal-700 text-xs font-medium transition-colors"
                            >
                              <Eye size={13} />
                              Réaliser
                            </button>
                          )}
                          {op.statut !== 'valide' && (
                            <button
                              onClick={() => handleValider(op)}
                              title="Valider"
                              className="flex items-center gap-1 px-2 py-1 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-medium transition-colors"
                            >
                              <Check size={13} />
                              Valider
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Planifier */}
      {showPlanifier && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="px-5 py-4 border-b flex-shrink-0 flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                <Plus size={18} className="text-teal-700" />
                Planifier une opération
              </h2>
              <button
                onClick={() => setShowPlanifier(false)}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold leading-none"
              >
                &times;
              </button>
            </div>
            <form onSubmit={handlePlanifier} className="flex flex-col flex-1 min-h-0">
              <div className="p-5 overflow-y-auto flex-1 min-h-0 grid grid-cols-2 gap-4 content-start">
                {/* Type */}
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Type d'opération *</label>
                  <select
                    required
                    value={form.typeOperation}
                    onChange={e => handleTypeChange(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="">Sélectionner...</option>
                    {Object.entries(TYPE_LABELS).map(([k, v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
                {/* Date */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Date *</label>
                  <input
                    type="date"
                    required
                    value={form.date}
                    onChange={e => handleFormChange('date', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                {/* Parcelle */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Parcelle</label>
                  <select
                    value={form.parcelleId ?? ''}
                    onChange={e => handleFormChange('parcelleId', e.target.value || undefined)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="">Toutes</option>
                    {flatParcelles.map(p => (
                      <option key={p.id} value={p.id}>{p.nom} ({p.code})</option>
                    ))}
                  </select>
                </div>
                {/* Campagne */}
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Campagne</label>
                  <select
                    value={form.campagneId ?? campagneActive ?? ''}
                    onChange={e => handleFormChange('campagneId', e.target.value || undefined)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                  >
                    <option value="">Aucune</option>
                    {campagnes.map(c => (
                      <option key={c.id} value={c.id}>{c.libelle} ({c.code})</option>
                    ))}
                  </select>
                </div>
                {/* Champs dynamiques */}
                {form.typeOperation && (
                  <ChampsPlanifie type={form.typeOperation} form={form} onChange={handleFormChange} />
                )}
              </div>
              <div className="px-5 py-3 border-t flex justify-end gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setShowPlanifier(false)}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 rounded-lg bg-teal-700 hover:bg-teal-800 text-white text-sm font-medium disabled:opacity-50 transition-colors"
                >
                  {submitting ? 'Enregistrement...' : 'Planifier'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Saisir le réalisé */}
      {showRealiser && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="px-5 py-4 border-b flex-shrink-0 flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
                <Check size={18} className="text-emerald-600" />
                Saisir le réalisé — {TYPE_LABELS[showRealiser.typeOperation] ?? showRealiser.typeOperation}
              </h2>
              <button
                onClick={() => setShowRealiser(null)}
                className="text-gray-400 hover:text-gray-600 text-xl font-bold leading-none"
              >
                &times;
              </button>
            </div>
            <form onSubmit={handleRealiser} className="flex flex-col flex-1 min-h-0">
              <div className="p-5 overflow-y-auto flex-1 min-h-0">
                <p className="text-xs text-gray-500 mb-4">
                  Planifié le {new Date(showRealiser.date).toLocaleDateString('fr-FR')}
                  {showRealiser.superficiePlanifiee != null && ` — ${showRealiser.superficiePlanifiee} ha planifiés`}
                </p>
                <div className="grid grid-cols-2 gap-4">
                  <ChampsRealise
                    type={showRealiser.typeOperation}
                    form={realiseForm}
                    onChange={handleRealiseChange}
                  />
                </div>
              </div>
              <div className="px-5 py-3 border-t flex justify-end gap-2 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setShowRealiser(null)}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50 transition-colors"
                >
                  {submitting ? 'Enregistrement...' : 'Enregistrer le réalisé'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
